import React, { useState, useEffect } from "react"
import { Row, Col, Card, CardBody, Button, Table, Input, Label, Modal, ModalHeader, ModalBody, ModalFooter, Spinner, Alert } from "reactstrap"
import { useGetRouteTypesQuery, useCreateRouteTypeMutation, useUpdateRouteTypeMutation, useDeleteRouteTypeMutation } from "../../store/routeType/routeType.api"
import Swal from "sweetalert2"

const RouteTypeManager = ({ isOpen, toggle }) => {
  const [newRouteType, setNewRouteType] = useState({
    name: "",
    description: ""
  })
  
  const [editingRouteType, setEditingRouteType] = useState(null)
  const [showEditForm, setShowEditForm] = useState(false)
  
  const { data: routeTypesData, isLoading, error, refetch } = useGetRouteTypesQuery()
  const [createRouteType, { isLoading: isCreating }] = useCreateRouteTypeMutation()
  const [updateRouteType, { isLoading: isUpdating }] = useUpdateRouteTypeMutation()
  const [deleteRouteType, { isLoading: isDeleting }] = useDeleteRouteTypeMutation()
  
  const routeTypes = routeTypesData?.routeTypes || []
  
  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer)
      toast.addEventListener('mouseleave', Swal.resumeTimer)
    }
  })
  
  const handleInputChange = (e) => {
    const { name, value } = e.target
    
    if (showEditForm) {
      setEditingRouteType({
        ...editingRouteType,
        [name]: value
      })
    } else {
      setNewRouteType({
        ...newRouteType,
        [name]: value
      })
    }
  }
  
  const handleAddRouteType = async () => {
    if (!newRouteType.name) {
      Toast.fire({
        icon: 'error',
        title: 'Name is required'
      })
      return
    }
    
    try {
      await createRouteType(newRouteType).unwrap()
      Toast.fire({
        icon: 'success',
        title: 'Route type added successfully'
      })
      setNewRouteType({
        name: "",
        description: ""
      })
      refetch()
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: error.data?.message || 'Failed to add route type'
      })
    }
  }
  
  const handleEditRouteType = (routeType) => {
    setEditingRouteType(routeType)
    setShowEditForm(true)
  }
  
  const handleUpdateRouteType = async () => {
    if (!editingRouteType.name) {
      Toast.fire({
        icon: 'error',
        title: 'Name is required'
      })
      return
    }
    
    try {
      await updateRouteType(editingRouteType).unwrap()
      Toast.fire({
        icon: 'success',
        title: 'Route type updated successfully'
      })
      setShowEditForm(false)
      setEditingRouteType(null)
      refetch()
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: error.data?.message || 'Failed to update route type'
      })
    }
  }
  
  const handleDeleteRouteType = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteRouteType(id).unwrap()
          Toast.fire({
            icon: 'success',
            title: 'Route type deleted successfully'
          })
          refetch()
        } catch (error) {
          Toast.fire({
            icon: 'error',
            title: error.data?.message || 'Failed to delete route type'
          })
        }
      }
    })
  }
  
  const cancelEdit = () => {
    setShowEditForm(false)
    setEditingRouteType(null)
  }
  
  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Manage Route Types</ModalHeader>
      <ModalBody>
        {error && (
          <Alert color="danger">
            {error.data?.message || 'An error occurred while fetching route types'}
          </Alert>
        )}
        
        <Card>
          <CardBody>
            {showEditForm ? (
              <>
                <h5>Edit Route Type</h5>
                <Row className="mb-3">
                  <Col md={6}>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      value={editingRouteType?.name || ''}
                      onChange={handleInputChange}
                      placeholder="Enter route type name"
                    />
                  </Col>
                  <Col md={6}>
                    <Label htmlFor="description">Description (Optional)</Label>
                    <Input
                      type="text"
                      id="description"
                      name="description"
                      value={editingRouteType?.description || ''}
                      onChange={handleInputChange}
                      placeholder="Enter description"
                    />
                  </Col>
                </Row>
                <div className="d-flex">
                  <Button 
                    color="primary" 
                    className="me-2" 
                    onClick={handleUpdateRouteType}
                    disabled={isUpdating}
                  >
                    {isUpdating ? <Spinner size="sm" /> : 'Update'}
                  </Button>
                  <Button color="secondary" onClick={cancelEdit}>Cancel</Button>
                </div>
              </>
            ) : (
              <>
                <h5>Add New Route Type</h5>
                <Row className="mb-3">
                  <Col md={6}>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      value={newRouteType.name}
                      onChange={handleInputChange}
                      placeholder="Enter route type name"
                    />
                  </Col>
                  <Col md={6}>
                    <Label htmlFor="description">Description (Optional)</Label>
                    <Input
                      type="text"
                      id="description"
                      name="description"
                      value={newRouteType.description}
                      onChange={handleInputChange}
                      placeholder="Enter description"
                    />
                  </Col>
                </Row>
                <Button 
                  color="primary" 
                  onClick={handleAddRouteType}
                  disabled={isCreating}
                >
                  {isCreating ? <Spinner size="sm" /> : 'Add Route Type'}
                </Button>
              </>
            )}
          </CardBody>
        </Card>
        
        <Card className="mt-4">
          <CardBody>
            <h5>Route Types</h5>
            {isLoading ? (
              <div className="text-center p-3">
                <Spinner />
              </div>
            ) : routeTypes.length === 0 ? (
              <p className="text-center">No route types found</p>
            ) : (
              <Table responsive bordered>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {routeTypes.map((routeType) => (
                    <tr key={routeType.id}>
                      <td>{routeType.name}</td>
                      <td>{routeType.description || '-'}</td>
                      <td>
                        <Button
                          color="info"
                          size="sm"
                          className="me-2"
                          onClick={() => handleEditRouteType(routeType)}
                        >
                          Edit
                        </Button>
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => handleDeleteRouteType(routeType.id)}
                          disabled={isDeleting}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>Close</Button>
      </ModalFooter>
    </Modal>
  )
}

export default RouteTypeManager 